import { useContext, useEffect, useMemo, useState } from 'react';
import {
    Alert, Badge, Box, Button, Card, Container, DirectionProvider, Divider, Group, Modal, Paper,
    SegmentedControl, SimpleGrid, Skeleton, Stack, TagsInput, Tabs, Text, TextInput, Title,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconInfoCircle, IconPackage, IconSearch, IconSpeakerphone, IconX } from '@tabler/icons-react';
import { collection, doc, getDocs, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { ConfigContext, GlobalDataContext, MyUserDataContext } from '../../contexts';
import { useFirestore } from '../../helpers/firebaseContext';
import { CustomClaims, Fund, FundAnnouncement } from '../../types';
import { CATALOGUE_COPY, CatalogueLanguage, MANAGER_COPY } from './catalogue_copy';
import {
    CatalogueFilters, CatalogueFund, EMPTY_FILTERS, PortfolioProduct, activeManagementOptions, collectFacets,
    filterFunds, filterPortfolios, formatAmount, formatDate, formatNumber, formatPercent, hasActiveFilters,
    managementOptionDescription, managementOptionName, portfolioDescription, portfolioDisplayName, portfolioMinimumAmount,
    selectCatalogueFunds, selectCataloguePortfolios, useFundDistributions, useFundPerformance,
} from './catalogue_helpers';
import { FundCard, PortfolioCard } from './catalogue_cards';
import { CatalogueFilterPills } from './catalogue_filter_pills';
import FundDrawer from './FundDrawer';

/** Announcements pulled per fund for the portal feed the WhatsApp notice points RMs at. */
const ANNOUNCEMENTS_PER_FUND = 5;
const ANNOUNCEMENTS_SHOWN = 25;

type FeedAnnouncement = FundAnnouncement & { fundName: string };

const ProductCatalogue = () => {
    const db = useFirestore();
    const globalData = useContext(GlobalDataContext);
    const userData = useContext(MyUserDataContext);
    const config = useContext(ConfigContext);

    const claims = (userData.firebaseTokenResult?.claims.customClaims || {}) as CustomClaims;
    const currentEmail = userData.user?.email?.toLowerCase() || '';

    const [language, setLanguage] = useState<CatalogueLanguage>('en');
    const [filters, setFilters] = useState<CatalogueFilters>(EMPTY_FILTERS);
    // Debounced so typing does not re-filter and re-render every grid card per keystroke.
    const [debouncedSearch] = useDebouncedValue(filters.search, 200);
    const [tab, setTab] = useState<string | null>('products');

    const [selectedFund, setSelectedFund] = useState<CatalogueFund | null>(null);
    const [selectedPortfolio, setSelectedPortfolio] = useState<PortfolioProduct | null>(null);
    /**
     * The assignment modal owns its target fund rather than reading it back off the drawer.
     *
     * The modal is portaled to <body>, so it sits *outside* the drawer's DOM subtree — with
     * Mantine's default `closeOnClickOutside`, touching the modal closed the drawer, cleared
     * `selectedFund`, and left Save silently doing nothing.
     */
    const [managerTarget, setManagerTarget] = useState<{ id: string; name: string } | null>(null);
    const [managerEmails, setManagerEmails] = useState<string[]>([]);
    /**
     * Text typed into the TagsInput but not yet turned into a pill.
     *
     * `acceptValueOnBlur` commits it when the field loses focus, but that commit is a
     * setState racing the Save click's own handler, which closes over the previous
     * `managerEmails`. Tracking it here means Save uses what the user can see either way.
     */
    const [managerDraft, setManagerDraft] = useState('');
    const [savingManagers, setSavingManagers] = useState(false);

    const [feed, setFeed] = useState<FeedAnnouncement[] | null>(null);
    const [feedLoading, setFeedLoading] = useState(false);

    const copy = CATALOGUE_COPY[language];
    const direction = language === 'ar' ? 'rtl' : 'ltr';
    const fundsLoading = !globalData.fundsMap;

    const funds = useMemo(() => selectCatalogueFunds(globalData.fundsMap), [globalData.fundsMap]);
    const portfolios = useMemo(() => selectCataloguePortfolios(config.portfolio_service), [config.portfolio_service]);
    const facets = useMemo(() => collectFacets(funds), [funds]);
    const performance = useFundPerformance(db, funds);
    const distributions = useFundDistributions(db, funds);

    const appliedFilters = useMemo(() => ({ ...filters, search: debouncedSearch }), [filters, debouncedSearch]);
    const visibleFunds = useMemo(
        () => filterFunds(funds, appliedFilters, language, distributions),
        [funds, appliedFilters, language, distributions],
    );
    const fundGroups = useMemo(() => {
        const byCurrency = new Map<string, CatalogueFund[]>();
        for (const fund of visibleFunds) {
            const currency = `${fund.currency || ''}`.trim().toUpperCase() || copy.currency;
            byCurrency.set(currency, [...(byCurrency.get(currency) || []), fund]);
        }
        return [...byCurrency.entries()]
            .sort(([left], [right]) => left.localeCompare(right))
            .map(([currency, currencyFunds]) => ({
                currency,
                // Series IDs control order inside the currency group but remain hidden.
                funds: currencyFunds.sort((a, b) => a.docId.localeCompare(b.docId, undefined, { numeric: true })),
            }));
    }, [visibleFunds, copy.currency]);
    const visiblePortfolios = useMemo(() => filterPortfolios(portfolios, appliedFilters), [portfolios, appliedFilters]);

    /**
     * The selected fund is held by id, not by value: `fundsMap` is a live snapshot, so a
     * NAV update or a manager assignment while the drawer is open must be reflected
     * rather than leaving a stale copy on screen.
     */
    const openFund = selectedFund
        ? funds.find((fund) => fund.docId === selectedFund.docId) || selectedFund
        : null;

    // Portfolio minimums live on the management options, not the portfolio document.
    const selectedMinimum = selectedPortfolio ? portfolioMinimumAmount(selectedPortfolio) : null;

    const canAssignManagers = !!(claims.admin || claims.oper);
    const canManageOpenFund = !!(
        openFund
        && claims.fund_manager
        && openFund.fund_manager_emails?.some((email) => `${email}`.toLowerCase() === currentEmail)
    );

    /** Loaded on demand: this is N queries, and most visits never open the feed. */
    useEffect(() => {
        if (tab !== 'announcements' || feed !== null || !funds.length) return;
        let cancelled = false;
        setFeedLoading(true);
        Promise.allSettled(funds.map(async (fund) => {
            // Equality on `type` only: pairing it with orderBy would need a composite
            // index, and a fund has few enough announcements to order and cap in memory.
            const snapshot = await getDocs(query(
                collection(db, 'funds', fund.docId, 'updates'),
                where('type', '==', 'announcement'),
            ));
            return snapshot.docs
                .map((item) => ({
                    id: item.id,
                    fundName: (language === 'ar' ? fund.name_ar : fund.name_en) || fund.name || '',
                    ...item.data(),
                } as FeedAnnouncement))
                .sort((a, b) => (b.created_at?.toMillis?.() || 0) - (a.created_at?.toMillis?.() || 0))
                .slice(0, ANNOUNCEMENTS_PER_FUND);
        })).then((results) => {
            if (cancelled) return;
            const collected = results
                .flatMap((result) => (result.status === 'fulfilled' ? result.value : []))
                .sort((a, b) => (b.created_at?.toMillis?.() || 0) - (a.created_at?.toMillis?.() || 0))
                .slice(0, ANNOUNCEMENTS_SHOWN);
            setFeed(collected);
            setFeedLoading(false);
        });
        return () => { cancelled = true; };
        // `language` only changes the display name; refetching for it would be wasteful.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, feed, funds, db]);

    const saveManagers = async () => {
        // Never return silently: a disabled-looking Save button with no feedback is the
        // hardest kind of failure to report.
        if (!managerTarget || !canAssignManagers) {
            notifications.show({
                color: 'red',
                title: 'Could not update managers',
                message: managerTarget
                    ? 'Your account is not allowed to assign fund managers.'
                    : 'No fund selected. Close this dialog and reopen it from the fund.',
            });
            return;
        }
        const emails = [...new Set([...managerEmails, managerDraft.trim().toLowerCase()].filter(Boolean))];
        setSavingManagers(true);
        try {
            await updateDoc(doc(db, 'funds', managerTarget.id), {
                fund_manager_emails: emails,
                updated_at: serverTimestamp(),
            });
            setManagerTarget(null);
            setManagerDraft('');
            notifications.show({
                color: 'teal',
                title: 'Fund managers updated',
                message: emails.length
                    ? `${emails.length} manager(s) assigned to ${managerTarget.name}: ${emails.join(', ')}`
                    : `All assignments removed from ${managerTarget.name}.`,
            });
        } catch (error: any) {
            notifications.show({
                color: 'red',
                title: 'Could not update managers',
                message: error?.message || 'The update was rejected.',
                autoClose: false,
            });
        } finally {
            setSavingManagers(false);
        }
    };

    const updateFilter = <K extends keyof CatalogueFilters>(key: K, value: CatalogueFilters[K]) =>
        setFilters((current) => ({ ...current, [key]: value }));

    const filtersActive = hasActiveFilters(appliedFilters);

    const emptyState = (message: string) => (
        <Paper withBorder p="xl" radius="md">
            <Stack align="center" gap={4}>
                <Text c="dimmed">{message}</Text>
                {filtersActive && <Text size="sm" c="dimmed">{copy.noResultsHint}</Text>}
            </Stack>
        </Paper>
    );

    return (
        // Scoped to this page: `initialDirection` alone never touches document.documentElement,
        // so the surrounding portal chrome stays LTR. Keyed so switching language remounts it —
        // the provider seeds its state once from `initialDirection`.
        <DirectionProvider key={direction} initialDirection={direction} detectDirection={false}>
            <Container size="xl" py="lg" dir={direction}>
                <Group justify="space-between" mb="lg" align="flex-end" wrap="wrap">
                    <Box>
                        <Title order={2}>{copy.catalogue}</Title>
                        <Text c="dimmed" maw={640}>{copy.intro}</Text>
                    </Box>
                    <Group>
                        <Badge size="lg" variant="light">
                            {formatNumber(visibleFunds.length + visiblePortfolios.length, language)} {copy.products}
                        </Badge>
                        <SegmentedControl
                            value={language}
                            onChange={(value) => setLanguage(value as CatalogueLanguage)}
                            aria-label="Catalogue language"
                            data={[{ label: 'English', value: 'en' }, { label: 'العربية', value: 'ar' }]}
                        />
                    </Group>
                </Group>

                <Tabs value={tab} onChange={setTab} mb="lg">
                    <Tabs.List>
                        <Tabs.Tab value="products" leftSection={<IconPackage size={16} />}>{copy.catalogue}</Tabs.Tab>
                        <Tabs.Tab value="announcements" leftSection={<IconSpeakerphone size={16} />}>
                            {copy.announcementsFeed}
                        </Tabs.Tab>
                    </Tabs.List>

                    <Tabs.Panel value="products" pt="md">
                        <Paper withBorder p="md" radius="md" mb="lg">
                            <Stack gap="md">
                                <Group align="center" gap="sm" wrap="wrap">
                                    <TextInput
                                        style={{ flex: 1, minWidth: 240 }}
                                        leftSection={<IconSearch size={18} />}
                                        placeholder={copy.search}
                                        aria-label={copy.searchAria}
                                        value={filters.search}
                                        onChange={(event) => updateFilter('search', event.currentTarget.value)}
                                    />
                                    {filtersActive && (
                                        <Button
                                            variant="subtle"
                                            leftSection={<IconX size={16} />}
                                            onClick={() => setFilters(EMPTY_FILTERS)}
                                        >
                                            {copy.clearFilters}
                                        </Button>
                                    )}
                                </Group>
                                {/* Pills replace the old dropdowns: same filter state, but every
                                    option is visible at once with the number of funds behind it. */}
                                <CatalogueFilterPills
                                    funds={funds}
                                    filters={appliedFilters}
                                    facets={facets}
                                    language={language}
                                    distributions={distributions}
                                    onChange={updateFilter}
                                />
                            </Stack>
                        </Paper>

                        <Title order={3} mb="sm">{copy.funds}</Title>
                        {fundsLoading ? (
                            <SimpleGrid cols={{ base: 1, md: 2 }}>
                                {[0, 1, 2].map((index) => <Skeleton key={index} height={420} radius="lg" />)}
                            </SimpleGrid>
                        ) : visibleFunds.length ? (
                            <Stack gap="xl">
                                {fundGroups.map((group) => (
                                    <Box key={group.currency}>
                                        <Group mb="sm" gap="xs">
                                            <Title order={4}>{group.currency}</Title>
                                            <Badge variant="light">{formatNumber(group.funds.length, language)}</Badge>
                                        </Group>
                                        <SimpleGrid cols={{ base: 1, md: 2 }}>
                                            {group.funds.map((fund) => (
                                                <FundCard
                                                    key={fund.docId}
                                                    fund={fund}
                                                    language={language}
                                                    performance={performance[fund.docId]}
                                                    dividends={distributions[fund.docId]}
                                                    onOpen={() => setSelectedFund(fund)}
                                                />
                                            ))}
                                        </SimpleGrid>
                                    </Box>
                                ))}
                            </Stack>
                        ) : emptyState(copy.noProducts)}

                        <Group justify="space-between" mt="xl" mb="sm" align="baseline">
                            <Title order={3}>{copy.portfolios}</Title>
                            <Text size="sm" c="dimmed">{formatNumber(visiblePortfolios.length, language)}</Text>
                        </Group>
                        {visiblePortfolios.length ? (
                            <SimpleGrid cols={{ base: 1, md: 2 }} mb="xl">
                                {visiblePortfolios.map((portfolio) => (
                                    <PortfolioCard
                                        key={portfolio.id}
                                        portfolio={portfolio}
                                        language={language}
                                        onOpen={() => setSelectedPortfolio(portfolio)}
                                    />
                                ))}
                            </SimpleGrid>
                        ) : emptyState(copy.noPortfolios)}
                    </Tabs.Panel>

                    <Tabs.Panel value="announcements" pt="md">
                        <Stack>
                            <Alert variant="light" icon={<IconInfoCircle size={16} />}>
                                {copy.announcementsFeed}
                            </Alert>
                            {feedLoading && [0, 1, 2].map((index) => <Skeleton key={index} height={90} radius="md" />)}
                            {feed?.map((announcement) => (
                                <Card key={announcement.id} withBorder radius="md" padding="md">
                                    <Group justify="space-between" align="flex-start" wrap="wrap">
                                        <Box>
                                            <Text fw={600}>{announcement.subject}</Text>
                                            <Text size="sm" c="dimmed">{announcement.fundName}</Text>
                                        </Box>
                                        <Text size="xs" c="dimmed">{formatDate(announcement.created_at, language)}</Text>
                                    </Group>
                                    <Text size="xs" c="dimmed" mt="xs" dir="ltr">
                                        {copy.by} {announcement.created_by}
                                    </Text>
                                </Card>
                            ))}
                            {!feedLoading && feed && !feed.length && emptyState(copy.noAnnouncements)}
                        </Stack>
                    </Tabs.Panel>
                </Tabs>

                <Modal
                    opened={!!selectedPortfolio}
                    onClose={() => setSelectedPortfolio(null)}
                    size="lg"
                    title={selectedPortfolio ? portfolioDisplayName(selectedPortfolio, language) : copy.portfolioInfo}
                >
                    {selectedPortfolio && (
                        <Stack>
                            <Group>
                                <Badge color="green">{copy.active}</Badge>
                                <Badge color="violet" variant="light">{copy.portfolioService}</Badge>
                                {selectedPortfolio.portfolio_type && (
                                    <Badge variant="outline">{selectedPortfolio.portfolio_type}</Badge>
                                )}
                            </Group>
                            <Text c="dimmed">
                                {(language === 'ar' ? selectedPortfolio.name_en : selectedPortfolio.name_ar) || ''}
                            </Text>
                            <Text>{portfolioDescription(selectedPortfolio, language) || copy.noDescription}</Text>

                            <SimpleGrid cols={2}>
                                <Paper withBorder p="sm" radius="md">
                                    <Text size="xs" c="dimmed">{copy.portfolioType}</Text>
                                    <Text fw={500}>{selectedPortfolio.portfolio_type || '—'}</Text>
                                </Paper>
                                <Paper withBorder p="sm" radius="md">
                                    <Text size="xs" c="dimmed">{copy.minimumAmount}</Text>
                                    <Text fw={500}>
                                        {selectedMinimum ? formatAmount(selectedMinimum.amount, language, selectedMinimum.currency) : '—'}
                                    </Text>
                                </Paper>
                            </SimpleGrid>

                            <Box>
                                <Text fw={600} mb="xs">{copy.managementOptions}</Text>
                                {activeManagementOptions(selectedPortfolio).map((option) => (
                                    <Paper key={option.id || option.portfolio_type} withBorder p="sm" mb="xs" radius="md">
                                        <Group justify="space-between" align="flex-start">
                                            <Text fw={500}>{managementOptionName(option, language)}</Text>
                                            {Number.isFinite(option.fees) && (
                                                <Badge variant="light">
                                                    {copy.managementFee}: {formatPercent(option.fees, language)}
                                                </Badge>
                                            )}
                                        </Group>
                                        <Text size="sm" c="dimmed">
                                            {managementOptionDescription(option, language) || copy.noDescription}
                                        </Text>
                                        <Group gap="xs" mt="xs">
                                            {!!option.minimum_amount?.amount && (
                                                <Badge size="sm" variant="outline">
                                                    {copy.minimumAmount}:{' '}
                                                    {formatAmount(option.minimum_amount.amount, language, option.minimum_amount.currency)}
                                                </Badge>
                                            )}
                                            {!!option.allowed_currencies?.length && (
                                                <Badge size="sm" variant="outline" color="gray">
                                                    {copy.currencies}: {option.allowed_currencies.map((entry) => entry.currency).join(', ')}
                                                </Badge>
                                            )}
                                            {!!option.risk_levels?.length && (
                                                <Badge size="sm" variant="outline" color="gray">
                                                    {copy.riskLevels}: {formatNumber(option.risk_levels.length, language)}
                                                </Badge>
                                            )}
                                        </Group>
                                    </Paper>
                                ))}
                                {!activeManagementOptions(selectedPortfolio).length && (
                                    <Text c="dimmed" size="sm">{copy.noDescription}</Text>
                                )}
                            </Box>
                        </Stack>
                    )}
                </Modal>

                {openFund && (
                    <FundDrawer
                        fund={openFund}
                        language={language}
                        canManage={canManageOpenFund}
                        canAssignManagers={canAssignManagers}
                        isAdmin={!!claims.admin}
                        currentEmail={currentEmail}
                        dividends={distributions[openFund.docId]}
                        onClose={() => setSelectedFund(null)}
                        managerModalOpen={!!managerTarget}
                        onAssignManagers={() => {
                            setManagerEmails(openFund.fund_manager_emails || []);
                            setManagerDraft('');
                            setManagerTarget({
                                id: openFund.docId,
                                name: openFund.name_en || openFund.name || openFund.docId,
                            });
                        }}
                    />
                )}

                {/* Manager assignment is operational tooling: English and LTR. */}
                <Modal
                    opened={!!managerTarget}
                    onClose={() => setManagerTarget(null)}
                    title={`${MANAGER_COPY.assignManagersTitle}${managerTarget ? ` — ${managerTarget.name}` : ''}`}
                    /* Above the drawer (200) but below Mantine's popover (300) and
                       notification (400) levels, so the TagsInput dropdown and any error
                       toast still land on top of this modal. */
                    zIndex={250}
                >
                    <Box dir="ltr">
                        <TagsInput
                            label={MANAGER_COPY.managerEmails}
                            description={MANAGER_COPY.managerHelp}
                            placeholder={MANAGER_COPY.managerPlaceholder}
                            value={managerEmails}
                            onChange={(values) => setManagerEmails(
                                [...new Set(values.map((email) => email.trim().toLowerCase()).filter(Boolean))],
                            )}
                            searchValue={managerDraft}
                            onSearchChange={setManagerDraft}
                            splitChars={[',', ' ', ';']}
                        />
                        <Divider my="md" />
                        <Group justify="flex-end">
                            <Button variant="default" onClick={() => setManagerTarget(null)}>
                                {MANAGER_COPY.cancel}
                            </Button>
                            <Button onClick={saveManagers} loading={savingManagers}>
                                {MANAGER_COPY.save}
                            </Button>
                        </Group>
                    </Box>
                </Modal>
            </Container>
        </DirectionProvider>
    );
};

export default ProductCatalogue;
